/**
 * The core throws for programmer errors only. Everything a caller could
 * plausibly get wrong — a malformed FEN, an illegal move, an unparseable PGN —
 * comes back as a `Result`.
 */
export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E }

export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value })
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error })

export const isOk = <T, E>(r: Result<T, E>): r is { ok: true; value: T } => r.ok
export const isErr = <T, E>(r: Result<T, E>): r is { ok: false; error: E } => !r.ok

export const map = <T, U, E>(r: Result<T, E>, f: (value: T) => U): Result<U, E> =>
  r.ok ? ok(f(r.value)) : r

export const flatMap = <T, U, E, F>(
  r: Result<T, E>,
  f: (value: T) => Result<U, F>,
): Result<U, E | F> => (r.ok ? f(r.value) : r)

/** For tests and call sites that have already proven the value is present. */
export const unwrap = <T, E>(r: Result<T, E>): T => {
  if (r.ok) return r.value
  throw new Error(`unwrap() on an error Result: ${JSON.stringify(r.error)}`)
}

export const unwrapOr = <T, E>(r: Result<T, E>, fallback: T): T => (r.ok ? r.value : fallback)

/**
 * Placed in the default arm of every `switch` over a discriminated union, so
 * adding a variant turns every incomplete match into a compile error.
 */
export const assertNever = (x: never, context = 'value'): never => {
  throw new Error(`Unhandled ${context}: ${JSON.stringify(x)}`)
}
